import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';
const ComparisonTable = () => {
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string | null>('experience');
  const handleTooltipToggle = (id: string) => {
    if (activeTooltip === id) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(id);
    }
  };
  const sections = [{
    id: 'experience',
    name: 'Customer Experience'
  }, {
    id: 'sustainability',
    name: 'Sustainability'
  }, {
    id: 'operations',
    name: 'Operations & Tech'
  }, {
    id: 'branding',
    name: 'Branding & Value'
  }];
  // Data structure for comparison features
  const features = [{
    section: 'experience',
    name: 'Cost per serving',
    tooltip: 'Typical cost for 12–16oz premium beverage.',
    smartsoda: {
      text: 'Low ($0.20–0.50 per premium drink)',
      status: 'good'
    },
    dispensor: {
      text: 'Medium (varies by plan)',
      status: 'medium'
    },
    bottles: {
      text: 'High (brand premiums + freight)',
      status: 'bad'
    },
    pantry: {
      text: 'High (service & brand markup)',
      status: 'bad'
    }
  }, {
    section: 'experience',
    name: 'Customer satisfaction',
    tooltip: 'Quality and customer satisfaction with beverage options.',
    smartsoda: {
      text: 'High (premium options + customization)',
      status: 'good'
    },
    dispensor: {
      text: 'Good (limited options)',
      status: 'medium'
    },
    bottles: {
      text: 'Mixed (stock dependent)',
      status: 'medium'
    },
    pantry: {
      text: 'Mixed (curation dependent)',
      status: 'medium'
    }
  }, {
    section: 'experience',
    name: 'Beverage variety',
    tooltip: 'Range of options without buying separate SKUs.',
    smartsoda: {
      text: 'Wide (still, sparkling, 29+ flavors)',
      status: 'good'
    },
    dispensor: {
      text: 'Moderate',
      status: 'medium'
    },
    bottles: {
      text: 'Limited (per SKU)',
      status: 'bad'
    },
    pantry: {
      text: 'Curated (pay for variety)',
      status: 'medium'
    }
  }, {
    section: 'experience',
    name: 'Premium presentation',
    tooltip: 'Visual appeal and premium feel of beverage service.',
    smartsoda: {
      text: 'High (customizable & elegant)',
      status: 'good'
    },
    dispensor: {
      text: 'Medium (standard options)',
      status: 'medium'
    },
    bottles: {
      text: 'Brand-dependent',
      status: 'medium'
    },
    pantry: {
      text: 'Variable',
      status: 'medium'
    }
  }, {
    section: 'experience',
    name: 'Customer dwell time',
    tooltip: 'Impact on how long customers stay in showroom.',
    smartsoda: {
      text: 'Increases by 15-27%',
      status: 'good'
    },
    dispensor: {
      text: 'Moderate increase',
      status: 'medium'
    },
    bottles: {
      text: 'Minimal impact',
      status: 'medium'
    },
    pantry: {
      text: 'Variable impact',
      status: 'medium'
    }
  }, {
    section: 'sustainability',
    name: 'Plastic waste reduction',
    tooltip: 'Single-use containers displaced.',
    smartsoda: {
      text: '90%+ reduction',
      status: 'good'
    },
    dispensor: {
      text: '90%+ reduction',
      status: 'good'
    },
    bottles: {
      text: 'Low',
      status: 'bad'
    },
    pantry: {
      text: 'Low–Moderate',
      status: 'medium'
    }
  }, {
    section: 'sustainability',
    name: 'CO₂ footprint',
    tooltip: 'Transport & packaging emissions.',
    smartsoda: {
      text: 'Low',
      status: 'good'
    },
    dispensor: {
      text: 'Low',
      status: 'good'
    },
    bottles: {
      text: 'High',
      status: 'bad'
    },
    pantry: {
      text: 'Medium–High',
      status: 'medium'
    }
  }, {
    section: 'sustainability',
    name: 'Brand alignment',
    tooltip: 'Support for sustainable brand messaging.',
    smartsoda: {
      text: 'Strong sustainability story',
      status: 'good'
    },
    dispensor: {
      text: 'Good sustainability story',
      status: 'good'
    },
    bottles: {
      text: 'Poor alignment',
      status: 'bad'
    },
    pantry: {
      text: 'Mixed messaging',
      status: 'medium'
    }
  }, {
    section: 'operations',
    name: 'IoT monitoring & auto-reorder',
    tooltip: 'Live status and alerts.',
    smartsoda: {
      text: 'Yes',
      status: 'good'
    },
    dispensor: {
      text: 'Yes',
      status: 'good'
    },
    bottles: {
      text: 'No',
      status: 'bad'
    },
    pantry: {
      text: 'Manual',
      status: 'medium'
    }
  }, {
    section: 'operations',
    name: 'Footprint & storage',
    tooltip: 'Space required in showroom environment.',
    smartsoda: {
      text: 'Small',
      status: 'good'
    },
    dispensor: {
      text: 'Small',
      status: 'good'
    },
    bottles: {
      text: 'Large',
      status: 'bad'
    },
    pantry: {
      text: 'Large',
      status: 'bad'
    }
  }, {
    section: 'operations',
    name: 'Self-service options',
    tooltip: 'Ability for customers to serve themselves.',
    smartsoda: {
      text: 'Fast & Intuitive',
      status: 'good'
    },
    dispensor: {
      text: 'Fast',
      status: 'good'
    },
    bottles: {
      text: 'Staff-dependent',
      status: 'medium'
    },
    pantry: {
      text: 'Staff-dependent',
      status: 'medium'
    }
  }, {
    section: 'operations',
    name: 'Cost model clarity',
    tooltip: 'Predictable monthly spend.',
    smartsoda: {
      text: 'Transparent + high ROI',
      status: 'good'
    },
    dispensor: {
      text: 'Subscription; variable costs',
      status: 'medium'
    },
    bottles: {
      text: 'Volatile',
      status: 'bad'
    },
    pantry: {
      text: 'Service fees + markup',
      status: 'medium'
    }
  }, {
    section: 'branding',
    name: 'Brand customization',
    tooltip: 'Custom options available to match your brand.',
    smartsoda: {
      text: 'Yes (custom menus & displays)',
      status: 'good'
    },
    dispensor: {
      text: 'Limited',
      status: 'medium'
    },
    bottles: {
      text: 'Label-driven',
      status: 'medium'
    },
    pantry: {
      text: 'Vendor-driven',
      status: 'medium'
    }
  }, {
    section: 'branding',
    name: 'Perceived value',
    tooltip: 'How customers perceive the amenity.',
    smartsoda: {
      text: 'Premium experience',
      status: 'good'
    },
    dispensor: {
      text: 'Standard amenity',
      status: 'medium'
    },
    bottles: {
      text: 'Expected service',
      status: 'medium'
    },
    pantry: {
      text: 'Variable perception',
      status: 'medium'
    }
  }, {
    section: 'branding',
    name: 'Sales conversion impact',
    tooltip: 'Effect on closing sales.',
    smartsoda: {
      text: 'Significant positive impact',
      status: 'good'
    },
    dispensor: {
      text: 'Moderate impact',
      status: 'medium'
    },
    bottles: {
      text: 'Minimal impact',
      status: 'medium'
    },
    pantry: {
      text: 'Variable impact',
      status: 'medium'
    }
  }];
  // Helper function to render status indicator
  const renderStatus = status => {
    if (status === 'good') return <span className="text-green-600" data-id="element-8">✓</span>;
    if (status === 'medium') return <span className="text-yellow-500" data-id="element-9">~</span>;
    if (status === 'bad') return <span className="text-red-500" data-id="element-10">✗</span>;
    return null;
  };
  return <section className="py-20 bg-gray-50" id="comparison" data-id="element-11">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-12">
        <div className="text-center mb-16" data-id="element-13">
          <h2 className="text-3xl font-bold text-navy-950 sm:text-4xl" data-id="element-14">
            SmartSoda vs Alternatives
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-15">
            See why SmartSoda is the premium choice for showroom beverage
            programs
          </p>
        </div>
        {/* Desktop/Tablet View - Standard Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-16">
          <table className="min-w-full bg-white rounded-xl shadow-md overflow-hidden" data-id="element-17">
            <thead data-id="element-18">
              <tr className="bg-blue-50" data-id="element-19">
                <th className="px-6 py-4 text-left text-navy-950 font-semibold text-lg border-b" data-id="element-20">
                  Features
                </th>
                <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-21">
                  SmartSoda
                </th>
                <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-22">
                  Beverage Dispensor
                </th>
                <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-23">
                  Bottles & Cans
                </th>
                <th className="px-6 py-4 text-center text-navy-950 font-semibold text-lg border-b" data-id="element-24">
                  Beverage Service
                </th>
              </tr>
            </thead>
            <tbody data-id="element-25">
              {/* Customer Experience Section */}
              <tr className="bg-gray-100" data-id="element-26">
                <td colSpan={5} className="px-6 py-3 text-navy-950 font-bold" data-id="element-27">
                  Customer Experience
                </td>
              </tr>
              {features.filter(f => f.section === 'experience').map((feature, idx) => <tr key={`experience-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-28">
                    <td className="px-6 py-4 border-b text-navy-950 font-medium" data-id="element-29">
                      <div className="flex items-center" data-id="element-30">
                        {feature.name}
                        <button className="ml-2 text-gray-400 hover:text-blue-500 focus:outline-none" onClick={() => handleTooltipToggle(`${feature.section}-${idx}`)} aria-label={`More information about ${feature.name}`} data-id="element-31">
                          <InfoIcon className="h-4 w-4" data-id="element-32" />
                        </button>
                        {activeTooltip === `${feature.section}-${idx}` && <div className="absolute z-10 mt-2 ml-6 p-2 bg-white rounded-md shadow-lg text-sm text-gray-700 max-w-xs" data-id="element-33">
                            {feature.tooltip}
                          </div>}
                      </div>
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-34">
                      {feature.smartsoda.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.dispensor.status === 'good' ? 'text-green-600' : feature.dispensor.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-35">
                      {feature.dispensor.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-36">
                      {feature.bottles.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.pantry.status === 'good' ? 'text-green-600' : feature.pantry.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-37">
                      {feature.pantry.text}
                    </td>
                  </tr>)}
              {/* Sustainability Section */}
              <tr className="bg-gray-100" data-id="element-38">
                <td colSpan={5} className="px-6 py-3 text-navy-950 font-bold" data-id="element-39">
                  Sustainability
                </td>
              </tr>
              {features.filter(f => f.section === 'sustainability').map((feature, idx) => <tr key={`sustainability-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-40">
                    <td className="px-6 py-4 border-b text-navy-950 font-medium" data-id="element-41">
                      <div className="flex items-center" data-id="element-42">
                        {feature.name}
                        <button className="ml-2 text-gray-400 hover:text-blue-500 focus:outline-none" onClick={() => handleTooltipToggle(`${feature.section}-${idx}`)} aria-label={`More information about ${feature.name}`} data-id="element-43">
                          <InfoIcon className="h-4 w-4" data-id="element-44" />
                        </button>
                        {activeTooltip === `${feature.section}-${idx}` && <div className="absolute z-10 mt-2 ml-6 p-2 bg-white rounded-md shadow-lg text-sm text-gray-700 max-w-xs" data-id="element-45">
                            {feature.tooltip}
                          </div>}
                      </div>
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-46">
                      {feature.smartsoda.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.dispensor.status === 'good' ? 'text-green-600' : feature.dispensor.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-47">
                      {feature.dispensor.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-48">
                      {feature.bottles.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.pantry.status === 'good' ? 'text-green-600' : feature.pantry.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-49">
                      {feature.pantry.text}
                    </td>
                  </tr>)}
              {/* Operations & Tech Section */}
              <tr className="bg-gray-100" data-id="element-50">
                <td colSpan={5} className="px-6 py-3 text-navy-950 font-bold" data-id="element-51">
                  Operations & Tech
                </td>
              </tr>
              {features.filter(f => f.section === 'operations').map((feature, idx) => <tr key={`operations-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-52">
                    <td className="px-6 py-4 border-b text-navy-950 font-medium" data-id="element-53">
                      <div className="flex items-center" data-id="element-54">
                        {feature.name}
                        <button className="ml-2 text-gray-400 hover:text-blue-500 focus:outline-none" onClick={() => handleTooltipToggle(`${feature.section}-${idx}`)} aria-label={`More information about ${feature.name}`} data-id="element-55">
                          <InfoIcon className="h-4 w-4" data-id="element-56" />
                        </button>
                        {activeTooltip === `${feature.section}-${idx}` && <div className="absolute z-10 mt-2 ml-6 p-2 bg-white rounded-md shadow-lg text-sm text-gray-700 max-w-xs" data-id="element-57">
                            {feature.tooltip}
                          </div>}
                      </div>
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-58">
                      {feature.smartsoda.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.dispensor.status === 'good' ? 'text-green-600' : feature.dispensor.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-59">
                      {feature.dispensor.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-60">
                      {feature.bottles.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.pantry.status === 'good' ? 'text-green-600' : feature.pantry.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-61">
                      {feature.pantry.text}
                    </td>
                  </tr>)}
              {/* Branding & Value Section */}
              <tr className="bg-gray-100" data-id="element-62">
                <td colSpan={5} className="px-6 py-3 text-navy-950 font-bold" data-id="element-63">
                  Branding & Value
                </td>
              </tr>
              {features.filter(f => f.section === 'branding').map((feature, idx) => <tr key={`branding-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-id="element-64">
                    <td className="px-6 py-4 border-b text-navy-950 font-medium" data-id="element-65">
                      <div className="flex items-center" data-id="element-66">
                        {feature.name}
                        <button className="ml-2 text-gray-400 hover:text-blue-500 focus:outline-none" onClick={() => handleTooltipToggle(`${feature.section}-${idx}`)} aria-label={`More information about ${feature.name}`} data-id="element-67">
                          <InfoIcon className="h-4 w-4" data-id="element-68" />
                        </button>
                        {activeTooltip === `${feature.section}-${idx}` && <div className="absolute z-10 mt-2 ml-6 p-2 bg-white rounded-md shadow-lg text-sm text-gray-700 max-w-xs" data-id="element-69">
                            {feature.tooltip}
                          </div>}
                      </div>
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-70">
                      {feature.smartsoda.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.dispensor.status === 'good' ? 'text-green-600' : feature.dispensor.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-71">
                      {feature.dispensor.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-72">
                      {feature.bottles.text}
                    </td>
                    <td className={`px-6 py-4 border-b text-center ${feature.pantry.status === 'good' ? 'text-green-600' : feature.pantry.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-73">
                      {feature.pantry.text}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {/* Mobile View - Card-based Layout */}
        <div className="md:hidden" data-id="element-74">
          {/* Section Tabs */}
          <div className="flex overflow-x-auto mb-4 pb-2 no-scrollbar" data-id="element-75">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`px-4 py-2 whitespace-nowrap rounded-lg mr-2 ${activeSection === section.id ? 'bg-blue-500 text-white font-medium' : 'bg-gray-200 text-gray-700'}`} data-id="element-76">
                {section.name}
              </button>)}
          </div>
          {/* Feature Cards */}
          <div className="space-y-4" data-id="element-77">
            {features.filter(feature => feature.section === activeSection).map((feature, idx) => <div key={`mobile-${feature.section}-${idx}`} className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-78">
                  <div className="px-4 py-3 bg-blue-50 flex items-center justify-between" data-id="element-79">
                    <h3 className="font-medium text-navy-950" data-id="element-80">
                      {feature.name}
                    </h3>
                    <button onClick={() => handleTooltipToggle(`mobile-${feature.section}-${idx}`)} className="text-gray-400" aria-label={`Info about ${feature.name}`} data-id="element-81">
                      <InfoIcon className="h-4 w-4" data-id="element-82" />
                    </button>
                  </div>
                  {activeTooltip === `mobile-${feature.section}-${idx}` && <div className="px-4 py-2 bg-blue-50 text-sm text-gray-700 border-t border-blue-100" data-id="element-83">
                      {feature.tooltip}
                    </div>}
                  <div className="divide-y divide-gray-100" data-id="element-84">
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-85">
                      <div className="font-medium" data-id="element-86">SmartSoda</div>
                      <div className={`flex items-center ${feature.smartsoda.status === 'good' ? 'text-green-600' : feature.smartsoda.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-87">
                        {renderStatus(feature.smartsoda.status)}
                        <span className="ml-2" data-id="element-88">{feature.smartsoda.text}</span>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-89">
                      <div className="font-medium" data-id="element-90">Beverage Dispensor</div>
                      <div className={`flex items-center ${feature.dispensor.status === 'good' ? 'text-green-600' : feature.dispensor.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-91">
                        {renderStatus(feature.dispensor.status)}
                        <span className="ml-2" data-id="element-92">{feature.dispensor.text}</span>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-93">
                      <div className="font-medium" data-id="element-94">Bottles & Cans</div>
                      <div className={`flex items-center ${feature.bottles.status === 'good' ? 'text-green-600' : feature.bottles.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-95">
                        {renderStatus(feature.bottles.status)}
                        <span className="ml-2" data-id="element-96">{feature.bottles.text}</span>
                      </div>
                    </div>
                    <div className="px-4 py-3 flex justify-between items-center" data-id="element-97">
                      <div className="font-medium" data-id="element-98">Beverage Service</div>
                      <div className={`flex items-center ${feature.pantry.status === 'good' ? 'text-green-600' : feature.pantry.status === 'medium' ? 'text-yellow-500' : 'text-red-500'}`} data-id="element-99">
                        {renderStatus(feature.pantry.status)}
                        <span className="ml-2" data-id="element-100">{feature.pantry.text}</span>
                      </div>
                    </div>
                  </div>
                </div>)}
          </div>
        </div>
        <p className="mt-4 text-sm text-gray-500 italic" data-id="element-101">
          Note: Capabilities vary by model and vendor. Cost ranges are
          indicative.
        </p>
        <div className="mt-12 text-center" data-id="element-102">
          <a href="#book-demo" className="inline-flex items-center justify-center px-8 py-4 border border-transparent text-base font-medium rounded-lg shadow-md text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all" data-id="element-103">
            Experience the SmartSoda Difference
          </a>
        </div>
      </div>
    </section>;
};
export default ComparisonTable;